import React, { useState } from 'react';
import { gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Search, Save, Pencil, Download, User, Phone, MapPin, StickyNote } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Panel, Field, Notice, Pill, Switch } from '../components/gtpl/GtplKit';

// Edits a subscriber's profile fields directly on GTPL Saathi's Modify.aspx — account
// number only (this page has no STB serial lookup on GTPL's own side). Every field not
// shown here (KYC docs, CAF type, parent-child link, signature) is carried through
// unchanged server-side — see gtplSaathiService.modifyProfile's comment.
export default function GtplSaathiModifyPage() {
  useDarkReady();
  const { hasModuleRight } = useAuth();
  const canModify = hasModuleRight('gtplSaathi', 'modify');

  const [accountNumber, setAccountNumber] = useState('');
  const [searching, setSearching] = useState(false);
  const [fields, setFields] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fetchingCrm, setFetchingCrm] = useState(false);

  const set = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target.value }));
  const text = (key, label, extra = {}) => (
    <Field label={label}><input className="form-control" value={fields[key] ?? ''} onChange={set(key)} {...extra} /></Field>
  );

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!accountNumber.trim()) { toast.error('Enter a GTPL account number'); return; }
    setSearching(true);
    setFields(null);
    try {
      const { data } = await gtplSaathiAPI.modifySearch(accountNumber.trim());
      setFields(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  // Pulls this account's CRM record (matched on catv.accountNumber — our own source of
  // truth) and autofills First/Last Name + Mobile + the address boxes, but only the ones
  // that don't already match — untouched fields (Middle Name, etc.) are left exactly as
  // they are. Mobile matters here beyond display: Save only raises the mobile-update
  // eSupport ticket when fields.mobile is non-empty, and GTPL's own search often returns
  // it blank (that's the whole reason the ticket exists), so without this the ticket would
  // silently never fire. CRM only tracks one flat `name`, so it's split as first word /
  // last word for the two name boxes, matching "Name + Sirname".
  const handleFetchFromCrm = async () => {
    if (!fields) return;
    setFetchingCrm(true);
    try {
      const { data } = await gtplSaathiAPI.modifyCrmLookup(accountNumber.trim());
      const crm = data.data;
      const nameParts = (crm.name || '').trim().split(/\s+/).filter(Boolean);
      const crmFname = nameParts[0] || '';
      const crmLname = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

      const updates = {};
      const changed = [];
      const maybeSet = (key, newVal, label) => {
        const current = (fields[key] || '').trim();
        const next = (newVal || '').trim();
        if (next && next !== current) { updates[key] = next; changed.push(label); }
      };
      maybeSet('fname', crmFname, 'First Name');
      maybeSet('lname', crmLname, 'Last Name');
      maybeSet('mobile', crm.mobile, 'Mobile');
      maybeSet('address1', crm.address1, 'Address Line 1');
      maybeSet('address2', crm.address2, 'Address Line 2');
      maybeSet('pincode', crm.pincode, 'Pincode');

      if (changed.length) {
        setFields((f) => ({ ...f, ...updates }));
        toast.success(`Updated from CRM: ${changed.join(', ')}`);
      } else {
        toast.success('Already matches CRM — no changes needed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'CRM lookup failed');
    } finally {
      setFetchingCrm(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await gtplSaathiAPI.modifyProfile(accountNumber.trim(), fields);
      toast.success(data.data?.message || 'Profile updated');
      // fields.mobile is never actually sent to Modify.aspx (see gtplSaathiService.modifyProfile —
      // GTPL rejects the whole save for any KYC-verified subscriber otherwise). Every save with a
      // mobile value instead raises a real eSupport ticket server-side (complaintType 540 — see
      // routes/gtplSaathi.js's POST /modify), which is the actual mechanism that updates it.
      const ticket = data.data?.ticket;
      if (ticket?.ok) {
        toast.success(`eSupport ticket raised — Request No ${ticket.ticketNumber}`);
      } else if (ticket && ticket.ok === false) {
        toast.error(`Profile updated, but the mobile-update eSupport ticket failed: ${ticket.message}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Screen narrow>
      <PageHead icon={Pencil} tone="info" title="Edit a customer on GTPL"
        sub="Look up the account, fix the details, save. Every save also raises GTPL's “Update Customer Mobile Number” ticket." />

      <Panel icon={Search} tone="info" title="1. Find the account">
        <form onSubmit={handleSearch} className="gx-actions" style={{ alignItems: 'flex-end' }}>
          <Field label="GTPL account number" style={{ flex: '1 1 220px' }}>
            <input className="form-control" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="GTPL account number" />
          </Field>
          <button type="submit" className="btn btn-primary" disabled={searching || !canModify}>
            <Search size={15} />{searching ? 'Looking up…' : 'Look up'}
          </button>
        </form>
        {!canModify && <div className="gx-hint" style={{ color: 'var(--g-bad)' }}>You don't have permission to modify profiles on GTPL Saathi.</div>}
      </Panel>

      {fields && (
        <form onSubmit={handleSave}>
          <Notice tone="adv" title="Entity" icon={Download}
            actions={
              <button type="button" className="btn btn-sm btn-adv" onClick={handleFetchFromCrm} disabled={fetchingCrm || !canModify}>
                <Download size={14} />{fetchingCrm ? 'Fetching…' : 'Fill name & address from CRM'}
              </button>}>
            {fields.entity || '—'}. Only boxes that differ from the CRM record are changed.
          </Notice>

          <Panel icon={User} tone="info" title="2. Customer">
            <div className="gx-form">
              <Field label="Customer type">
                <div className="gx-actions">
                  {[['RESI', 'Residential'], ['COMRCL', 'Commercial']].map(([val, label]) => (
                    <button key={val} type="button" aria-pressed={fields.custType === val}
                      className={`btn btn-sm ${fields.custType === val ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setFields((f) => ({ ...f, custType: val }))}>{label}</button>
                  ))}
                </div>
              </Field>
              <div className="gx-grid">
                {text('fname', 'First name')}
                {text('mname', 'Middle name')}
                {text('lname', 'Last name')}
              </div>
            </div>
          </Panel>

          <Panel icon={Phone} tone="ok" title="Contact" sub="Mobile goes to GTPL through a support ticket">
            <div className="gx-grid">
              {text('mobile', 'Mobile', { inputMode: 'tel' })}
              {text('landline', 'Landline', { placeholder: '—' })}
              {text('email', 'Email', { placeholder: '—', type: 'email' })}
            </div>
          </Panel>

          <Panel icon={MapPin} tone="warn" title="Address">
            <div className="gx-grid">
              {text('address1', 'Address line 1')}
              {text('address2', 'Address line 2')}
              {text('pincode', 'Pincode', { inputMode: 'numeric' })}
            </div>
          </Panel>

          <Panel icon={StickyNote} tone="mute" title="Other">
            <div className="gx-form">
              <div className="gx-grid">
                {text('category', 'Category')}
                {text('notes', 'Notes')}
              </div>
              <div className="gx-actions" style={{ justifyContent: 'space-between' }}>
                <div><b>Auto-renew</b><div className="gx-hint" style={{ margin: 0 }}>Let GTPL renew this account by itself.</div></div>
                <span className="gx-actions"><Pill tone={fields.autoRenew ? 'ok' : 'mute'} dot>{fields.autoRenew ? 'On' : 'Off'}</Pill>
                  <Switch checked={!!fields.autoRenew} onChange={(v) => setFields((f) => ({ ...f, autoRenew: v }))} label="Auto-renew" /></span>
              </div>
            </div>
          </Panel>

          <div className="gx-sticky" style={{ margin: '0 0 14px', borderRadius: 14, border: '1px solid var(--border)' }}>
            <button type="submit" className="btn btn-primary" disabled={saving || !canModify}>
              <Save size={15} />{saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      )}
    </Screen>
  );
}
